import { SYSTEM_DISPOSITIONS } from '@100mslive/disposition-engine';

import { DEFAULT_DISPOSITIONS, WORKFLOW_DISPOSITIONS } from '@/constants/dispositions';

/**
 * Get dispositions for a specific workflow type
 * @param workflowType - The type field from the workflow
 * @returns Array of disposition options
 */
export function getDispositionsForWorkflow(workflowType?: string): string[] {
  if (!workflowType) {
    return DEFAULT_DISPOSITIONS;
  }

  // Normalize the workflow type to lowercase for case-insensitive matching
  const normalizedType = workflowType.toLowerCase();

  // Return specific dispositions if found, otherwise return default
  return WORKFLOW_DISPOSITIONS[normalizedType] || DEFAULT_DISPOSITIONS;
}

const dispositionLabelCollator = new Intl.Collator(undefined, {
  sensitivity: 'base',
  numeric: true,
});

/**
 * Comparator for disposition labels: ascending, case-insensitive, with numeric
 * runs compared as numbers ("Attempt 2" before "Attempt 10"). Every disposition
 * dropdown sorts its options with this, so they all read A→Z.
 */
export function compareDispositionLabels(a?: string, b?: string): number {
  return dispositionLabelCollator.compare(a ?? '', b ?? '');
}

/**
 * Whether a disposition code is one of the engine's SYSTEM dispositions —
 * telephony/infrastructure outcomes (101 "Not Connected - No Response",
 * 102 "Internal Server Error", 103 "Invalid Phone Number", 104 "Not Connected
 * - Call rejected or busy signal", 105 "Not Connected - Invalid phone number",
 * 106 "Not Connected - Telephony Error", plus 0 "Unassigned").
 *
 * These are written by the backend, never chosen by a human: `case_sync`
 * stamps one onto the case's `disposition_code` when a call fails to connect.
 * They describe *the dial*, not the benefit outcome, and none of them appear in
 * any workflow's selectable catalog (those live in the 1_0xx_xxx range), so a
 * dropdown fed from the catalog cannot render one — it falls back to its
 * placeholder and the field reads as empty while holding a value.
 *
 * Treat a system code as NO disposition: it must not pre-fill a selector,
 * satisfy a required-disposition gate, or be written onto a call.
 *
 * Sourced from the engine rather than a local literal so codes added upstream
 * are picked up without a dashboard change.
 */
export function isSystemDispositionCode(code: number | null | undefined): boolean {
  if (typeof code !== 'number') return false;
  return Object.prototype.hasOwnProperty.call(SYSTEM_DISPOSITIONS, String(code));
}
